/**
 * Session-ready gate for the webview boot (FCM-2662 Phase 1).
 *
 * AppBridge navigates to the destination page immediately and establishes the
 * session (bridge token -> CreateSession) in the background, so the destination
 * chunk renders during the round trips instead of after them. The session is an
 * HttpOnly cookie every API call needs, so the axios request interceptor awaits
 * this gate before sending.
 *
 * Default state is RESOLVED: desktop login, account deletion, and every
 * non-webview flow never arm the gate and are unaffected. Only AppBridge arms it.
 *
 * The session-establishment requests themselves (CreateSession, the one-time
 * token mint) must bypass the gate via `skipSessionGate` in their axios config,
 * otherwise they deadlock waiting on themselves.
 */

let sessionReady: Promise<void> = Promise.resolve();
let settle: {
  resolve: () => void;
  reject: (err: unknown) => void;
} | null = null;

export const armSessionGate = (): void => {
  sessionReady = new Promise<void>((resolve, reject) => {
    settle = { resolve, reject };
  });
  // Swallow the "unhandled rejection" warning; every real awaiter still sees
  // the rejection through waitForSession().
  sessionReady.catch(() => {});
};

export const resolveSessionGate = (): void => {
  settle?.resolve();
  settle = null;
};

export const rejectSessionGate = (err: unknown): void => {
  settle?.reject(err);
  settle = null;
};

export const waitForSession = (): Promise<void> => sessionReady;
